// ============================================================
// useFeeReceipts Hooks
// ============================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSchool } from './useSchool';
import { feeReceiptsApi } from '@/api/feeReceipts.api';

export function useFeeReceipts(skip = 0, limit = 100) {
  const { currentSchool } = useSchool();
  const schoolId = currentSchool?.id;

  return useQuery({
    queryKey: ['feeReceipts', schoolId, skip, limit],
    queryFn: () => feeReceiptsApi.getReceipts(schoolId, skip, limit),
    enabled: !!schoolId,
  });
}

export function useStudentReceipts(studentId, options = {}) {
  const { currentSchool } = useSchool();
  const schoolId = currentSchool?.id;

  return useQuery({
    queryKey: ['studentReceipts', studentId, schoolId],
    queryFn: () => feeReceiptsApi.getStudentReceipts(schoolId, studentId),
    enabled: !!studentId && !!schoolId && (options.enabled !== false),
    ...options,
  });
}

export function useFeeReceiptById(receiptId, options = {}) {
  const { currentSchool } = useSchool();
  const schoolId = currentSchool?.id;

  return useQuery({
    queryKey: ['feeReceipt', receiptId, schoolId],
    queryFn: () => feeReceiptsApi.getReceiptById(schoolId, receiptId),
    enabled: !!receiptId && !!schoolId && (options.enabled !== false),
    ...options,
  });
}

export function useCreateFeeReceipt() {
  const { currentSchool } = useSchool();
  const schoolId = currentSchool?.id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => feeReceiptsApi.createReceipt(schoolId, payload),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(['feeReceipts', schoolId]);
      queryClient.invalidateQueries(['studentReceipts', variables.student_id, schoolId]);
      queryClient.invalidateQueries(['studentFeeSummary', variables.student_id, schoolId]);
      queryClient.invalidateQueries(['studentInstallments', variables.student_id, schoolId]);
      queryClient.invalidateQueries(['pendingFees', schoolId]);
      queryClient.invalidateQueries(['transactions', schoolId]);
    },
  });
}

export function useCancelFeeReceipt() {
  const { currentSchool } = useSchool();
  const schoolId = currentSchool?.id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (receiptId) => feeReceiptsApi.cancelReceipt(schoolId, receiptId),
    onSuccess: (data, receiptId) => {
      queryClient.invalidateQueries(['feeReceipts', schoolId]);
      queryClient.invalidateQueries(['studentReceipts']);
      queryClient.invalidateQueries(['studentFeeSummary']);
      queryClient.invalidateQueries(['studentInstallments']);
      queryClient.invalidateQueries(['pendingFees', schoolId]);
      queryClient.invalidateQueries(['transactions', schoolId]);
    },
  });
}
